import React from 'react';
import { PrismicRichText } from '@prismicio/react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { TEXT_STYLES, TextVariant, TYPOGRAPHY_MOBILE_BREAKPOINT } from '@components/shared/design/Typography';
import { PageSlice, SliceHeadingMessage } from '@contracts';
import Colors from '../Colors';

interface Props {
  slice: PageSlice;
}

const HeadingMessage: React.FC<Props> = ({ slice }) => {
  const sliceDetails = slice.primary as SliceHeadingMessage;
  const background = sliceDetails.background_color || Colors.mint50;
  const textColor = sliceDetails.text_color1 || Colors.navyBlue;
  const headingText = sliceDetails.heading_text.richText;
  const topImage = sliceDetails.top_image;
  const bottomImage = sliceDetails.bottom_image;
  const headingPadding = sliceDetails.heading_vertical_padding?.text || '144px 0';
  const headingMobilePadding = sliceDetails.heading_vertical_padding_mobile?.text || '90px 0';

  if (!sliceDetails.heading_text || (sliceDetails.heading_text.richText.length === 0 && !sliceDetails.heading_text.text)) {
    return null;
  }

  return (
    <HeadingMessageContainer
      $background={background}
      $textColor={textColor}
      $headingPadding={headingPadding}
      $headingMobilePadding={headingMobilePadding}
    >
      {topImage?.gatsbyImageData && (
        <ImageWrap>
          <GatsbyImage image={topImage.gatsbyImageData} alt={topImage.alt || ''} />
        </ImageWrap>
      )}
      <PrismicRichText field={headingText} />
      {bottomImage?.gatsbyImageData && (
        <ImageWrap>
          <GatsbyImage image={bottomImage.gatsbyImageData} alt={bottomImage.alt || ''} />
        </ImageWrap>
      )}
    </HeadingMessageContainer>
  );
};

export default HeadingMessage;

interface HeadingMessageContainerProps {
  $background: string;
  $textColor: string;
  $headingPadding: string;
  $headingMobilePadding: string;
}

const HeadingMessageContainer = styled.div<HeadingMessageContainerProps>`
  background: ${(props) => props.$background};
  width: 100%;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: ${(props) => props.$textColor || Colors.cream};

  p {
    padding: ${(props) => props.$headingPadding};
  }

  @media (max-width: ${TYPOGRAPHY_MOBILE_BREAKPOINT}px) {
    p {
      padding: ${(props) => props.$headingMobilePadding};
    }
  }

  > * {
    ${TEXT_STYLES[TextVariant.Heading4]};
    width: 1200px;
    max-width: 90vw;
    color: inherit;

    @media (max-width: ${TYPOGRAPHY_MOBILE_BREAKPOINT}px) {
      ${TEXT_STYLES[TextVariant.Heading6]};
    }
  }
`;

const ImageWrap = styled.div`
  max-height: 100px;
`;
